import { matchPath, useLocation } from "react-router-dom";
import blogData from "../../../pages/Blogs/data";
import blogTemplates from "../../../pages/Blogs/TemplateData";
import faqData from "../../../pages/FAQ/FAQData";
import DynamicPage from "./DynamicPage";

const staticMetadata = {
  "/": {
    title: "RAF Bridging Finance & Settlement Advances | WeBridge",
    description:
      "Access part of your settled Road Accident Fund payout sooner with WeBridge RAF bridging finance. Apply for a fast, secure settlement advance.",
  },
  "/services": {
    title: "Bridging Finance Services in South Africa | WeBridge",
    description:
      "Explore WeBridge RAF claim advances, property bridging finance, attorney finance, advocate accounts factoring and business growth finance.",
    canonicalPath: "/Services",
  },
  "/aboutus": {
    title: "About WeBridge | Bridging Finance Specialists",
    description:
      "Learn about WeBridge, our experienced team and our approach to providing reliable, personalised bridging finance in South Africa.",
    canonicalPath: "/AboutUs",
  },
  "/contact": {
    title: "Contact WeBridge | Bridging Finance Enquiries",
    description:
      "Contact WeBridge in Centurion or George for help with RAF advances, property bridging finance, attorney finance and application enquiries.",
    canonicalPath: "/Contact",
  },
  "/careers": {
    title: "Careers at WeBridge | Join Our Team",
    description:
      "Explore career opportunities at WeBridge and learn how to follow our latest vacancies and company updates.",
    canonicalPath: "/Careers",
  },
  "/blog": {
    title: "RAF & Bridging Finance Insights | WeBridge Blog",
    description:
      "Read WeBridge insights on RAF claims, settlement delays, bridging finance, legal finance and accessing funds while you wait.",
    canonicalPath: "/Blog",
  },
  "/advocate-factoring": {
    title: "Advocate Accounts Factoring in South Africa | WeBridge",
    description:
      "Convert verified unpaid advocate invoices into working capital with confidential advocate accounts factoring from WeBridge.",
    canonicalPath: "/Advocate-Factoring",
  },
  "/propertey": {
    title: "Property Bridging Finance in South Africa | WeBridge",
    description:
      "Access funds before a property transfer completes with property bridging finance from WeBridge.",
    canonicalPath: "/Property-Bridging-Finance",
    robots: "noindex, follow",
  },
  "/email-disclaimer": {
    title: "Email Disclaimer | WeBridge",
    description:
      "Read the WeBridge email disclaimer, including confidentiality, cybercrime and malware notices.",
    canonicalPath: "/email-disclaimer",
    robots: "noindex, follow",
  },
  "/privacy": {
    title: "Privacy Policy | WeBridge",
    description:
      "Read how WeBridge collects, uses and protects personal information submitted through its website and services.",
    canonicalPath: "/Privacy",
    robots: "noindex, follow",
  },
  "/socialmedia": {
    title: "WeBridge News & Social Media Updates",
    description:
      "Catch up on WeBridge news, educational posts and updates about RAF claims, property finance and bridging finance services.",
    canonicalPath: "/SocialMedia",
  },
  "/faq": {
    title: "RAF Frequently Asked Questions | WeBridge",
    description:
      "Find answers about RAF claim timelines, settlement payments, claim status, RAF contact details and bridging finance.",
    canonicalPath: "/FAQ",
  },
  "/faq/raf-call-centre-number": {
    title: "RAF Call Centre Number, Hours & Contact Details | WeBridge",
    description:
      "Find the official RAF Call Centre number, operating hours, email address, SMS service and self-service portal details.",
    canonicalPath: "/faq/raf-call-centre-number",
  },
  "/raf-bridging-finance": {
    title: "RAF Bridging Finance After Settlement | WeBridge",
    description:
      "If your RAF claim is settled but payment is delayed, access part of your expected payout sooner with WeBridge RAF bridging finance.",
    canonicalPath: "/RAF-Bridging-Finance",
  },
  "/property-bridging-finance": {
    title: "Property Bridging Finance in South Africa | WeBridge",
    description:
      "Access funds while waiting for a property transfer to register with property bridging finance from WeBridge.",
    canonicalPath: "/Property-Bridging-Finance",
  },
  "/attorney-bridging-finance": {
    title: "RAF Attorney Fee Bridging Finance | WeBridge",
    description:
      "Maintain your law firm's cash flow with an advance on fees from settled RAF matters while you wait for payment.",
    canonicalPath: "/Attorney-Bridging-Finance",
  },
  "/business-growth-finance": {
    title: "Business Growth & Working Capital Finance | WeBridge",
    description:
      "Explore invoice discounting, purchase order funding and tender funding solutions for South African businesses.",
    canonicalPath: "/Business-Growth-Finance",
  },
};

const noIndexMetadata = {
  "/raf-advance": "RAF Advance Application",
  "/applynow": "Apply for WeBridge Finance",
  "/properteyform": "Property Bridging Finance Application",
  "/apply-now-attorney": "Attorney Finance Application",
  "/apply-now-property": "Property Finance Application",
  "/contactform": "Contact WeBridge",
  "/thankyou": "Application Received",
  "/th-raf-c": "RAF Application Received",
  "/th-prop-other": "Property Application Received",
  "/th-prop-sel": "Property Seller Application Received",
  "/th-aida": "AIDA Application Received",
  "/th-raf-a": "Attorney Application Received",
  "/th-adv-fac": "Advocate Factoring Application Received",
  "/template1": "WeBridge Article Template",
};

const slugify = (value) => value.replace(/\s+/g, "-").toLowerCase();

const cleanDescription = (value) => {
  const description = String(value || "")
    .replace(/\s+/g, " ")
    .trim();

  if (description.length <= 160) return description;

  return `${description.slice(0, 157).replace(/\s+\S*$/, "")}...`;
};

function articleMetadata(slug, pathname) {
  const article = [...blogData, ...blogTemplates].find(
    (item) => slugify(item.title) === slug,
  );

  if (!article) return null;

  return {
    title: `${article.title} | WeBridge`,
    description: cleanDescription(article.Intro || article.FrontHeading),
    canonicalPath: pathname,
    image: article.imgSrc,
    imageAlt: article.FrontHeading || article.title,
    type: "article",
    author: article.author,
  };
}

function faqMetadata(slug, pathname) {
  const faq = faqData.find((item) => slugify(item.category) === slug);

  if (!faq) return null;

  return {
    title: `${faq.question} | WeBridge`,
    description: cleanDescription(
      [...(faq.description || []), faq.p1, faq.p2].filter(Boolean).join(" "),
    ),
    canonicalPath:
      slug === "raf-call-centre-number"
        ? "/faq/raf-call-centre-number"
        : pathname,
    image: faq.ImgSrc,
    imageAlt: faq.question,
    type: "article",
  };
}

export default function RouteMetadata() {
  const location = useLocation();
  const pathname =
    location.pathname === "/" ? "/" : location.pathname.replace(/\/+$/, "");
  const routeKey = pathname.toLowerCase();

  let metadata = staticMetadata[routeKey];

  const articleMatch = matchPath(
    { path: "/Article/:name", caseSensitive: false, end: true },
    pathname,
  );
  if (articleMatch) {
    metadata = articleMetadata(
      articleMatch.params.name.toLowerCase(),
      pathname,
    );
  }

  const faqMatch = matchPath(
    { path: "/FAQArticle/:name", caseSensitive: false, end: true },
    pathname,
  );
  if (faqMatch) {
    metadata = faqMetadata(faqMatch.params.name.toLowerCase(), pathname);
  }

  if (routeKey === "/post") {
    metadata = {
      title: "WeBridge Blog Articles",
      description: "Browse RAF and bridging finance articles from WeBridge.",
      canonicalPath: "/Blog",
      robots: "noindex, follow",
    };
  }

  if (noIndexMetadata[routeKey]) {
    metadata = {
      title: `${noIndexMetadata[routeKey]} | WeBridge`,
      description:
        "This page supports a WeBridge application or customer request and is not intended for search results.",
      canonicalPath: pathname,
      robots: "noindex, follow",
    };
  }

  if (!metadata) {
    metadata = {
      title: "Page Not Found | WeBridge",
      description: "The requested WeBridge page could not be found.",
      canonicalPath: pathname,
      robots: "noindex, nofollow",
    };
  }

  return <DynamicPage {...metadata} />;
}
